import {
    type MouseEvent as ReactMouseEvent,
    useEffect,
    useRef,
    useState
} from "react";

import {
    dinoAssistantStateAssets
} from "../../assets/dino-assistant/dinoAssistantAssets.js";
import { useI18n } from "../../i18n/I18nProvider.js";
import { normalizeSpeechText } from "../../services/speech/index.js";
import {
    useDinoContext,
    useDinoContextTopic
} from "./context/DinoContextProvider.js";
import { openDinoAssistant } from "./dinoAssistantEvents.js";
import { getDinoSurfaceState } from "./dinoSurfaceState.js";

const ACTIVE_SURFACE_SOURCE = "active-surface";

const hintByLanguage = {
    fr: "Aide-moi ici",
    fa: "اینجا کمکم کن"
} as const;

const fallbackLabelByLanguage: Readonly<Record<"fr" | "fa", Record<string, string>>> = {
    fr: {
        home: "Cette carte",
        grammar: "Cette leçon",
        vocabulary: "Ce vocabulaire",
        travel: "Cette étape",
        journal: "Cet article",
        practice: "Cette activité",
        daily: "Cette session",
        profile: "Cette progression",
        shop: "Cette offre"
    },
    fa: {
        home: "این کارت",
        grammar: "این درس",
        vocabulary: "این واژگان",
        travel: "این مرحله",
        journal: "این مقاله",
        practice: "این تمرین",
        daily: "این جلسه",
        profile: "این پیشرفت",
        shop: "این پیشنهاد"
    }
};

function getDinoSurfaceLabel(surface: HTMLElement, fallback: string): string {
    const title = surface.querySelector<HTMLElement>(
        "[data-dino-title], h1, h2, h3, h4, strong"
    );

    return normalizeSpeechText(title?.innerText ?? "") || fallback;
}

/**
 * Contextual mascot used by rounded learning surfaces.
 *
 * Standalone cards expose a real keyboard button. Cards already wrapped in
 * a link keep the visual peek as a pointer shortcut so the DOM never nests
 * interactive controls; their link remains the keyboard action.
 */
function DinoSurfacePeek() {
    const { language } = useI18n();
    const context = useDinoContext();
    const topic = useDinoContextTopic();
    const peekRef = useRef<HTMLElement | null>(null);
    const [standaloneAction, setStandaloneAction] = useState(false);
    const state = getDinoSurfaceState(context.page?.kind);

    useEffect(
        () => {
            setStandaloneAction(!peekRef.current?.closest("a, button"));
        },
        []
    );

    if (!state) {
        return null;
    }

    const hint = hintByLanguage[language];

    function handleClick(event: ReactMouseEvent<HTMLElement>): void {
        const surface = event.currentTarget.closest(".dino-surface");

        if (!(surface instanceof HTMLElement)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const pageKind = context.page?.kind ?? "home";
        const fallbackLabel = fallbackLabelByLanguage[language][pageKind] ?? hint;
        const contextLabel = getDinoSurfaceLabel(surface, fallbackLabel);
        const releaseContext = topic.publish(
            {
                userAction: {
                    type: "request-contextual-help",
                    target: contextLabel,
                    occurredAt: new Date().toISOString()
                },
                metadata: {
                    surfaceKind: "card",
                    surfaceLabel: contextLabel
                }
            },
            ACTIVE_SURFACE_SOURCE
        );

        openDinoAssistant({
            readingTarget: surface,
            contextLabel,
            releaseContext
        });
    }

    const content = (
        <>
            <img
                src={dinoAssistantStateAssets[state]}
                alt=""
                draggable={false}
            />
            <span className="dino-surface-peek__hint">
                {hint}
            </span>
        </>
    );

    if (standaloneAction) {
        return (
            <button
                ref={element => {
                    peekRef.current = element;
                }}
                type="button"
                className="dino-surface-peek"
                data-dino-state={state}
                title={hint}
                aria-label={hint}
                onClick={handleClick}
            >
                {content}
            </button>
        );
    }

    return (
        <span
            ref={element => {
                peekRef.current = element;
            }}
            className="dino-surface-peek"
            data-dino-state={state}
            title={hint}
            aria-hidden="true"
            onClick={handleClick}
            onPointerDown={event => event.stopPropagation()}
        >
            {content}
        </span>
    );
}

export { DinoSurfacePeek };
